import type { ReactNode } from 'react';

import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import { Box, Button, Stack, Typography } from '@mui/material';
import { Link as RouterLink, Navigate, Outlet, useLocation } from 'react-router-dom';

import { useSession } from '@/shared/session';
import { LoadingState } from '@/shared/ui';

function FullScreen({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      {children}
    </Box>
  );
}

/** Renders a centred "you don't belong to any company" state for orphan accounts. */
function NoCompany() {
  const { logout } = useSession();
  return (
    <FullScreen>
      <Stack spacing={2} alignItems="center" textAlign="center" sx={{ maxWidth: 440 }}>
        <StorefrontRoundedIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        <Typography variant="h5">No company yet</Typography>
        <Typography variant="body2" color="text.secondary">
          Your account isn&apos;t a member of any company. Ask a company administrator to invite you,
          or create a new company.
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button component={RouterLink} to="/sign-up" variant="contained">
            Create a company
          </Button>
          <Button onClick={logout} startIcon={<LogoutRoundedIcon />} color="inherit">
            Sign out
          </Button>
        </Stack>
      </Stack>
    </FullScreen>
  );
}

/**
 * Route guard for the authenticated application shell.
 *
 * - `loading`        → full-screen loader while `GET /api/v1/me` resolves.
 * - `unauthenticated`→ redirect to the public landing page.
 * - no memberships   → orphan-account state.
 * - many tenants, none active → tenant-selection screen.
 * - otherwise        → render the protected routes.
 */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();

  if (session.status === 'loading') {
    return (
      <FullScreen>
        <LoadingState label="Loading your session…" />
      </FullScreen>
    );
  }

  if (session.status === 'unauthenticated') {
    return <Navigate to="/welcome" replace state={{ from: location.pathname }} />;
  }

  if (session.tenants.length === 0) {
    return <NoCompany />;
  }

  if (
    !session.activeTenant &&
    session.tenants.length > 1 &&
    location.pathname !== '/select-tenant'
  ) {
    return <Navigate to="/select-tenant" replace />;
  }

  return <Outlet />;
}
